import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { GlobalPrimitiveFieldId, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import type { FormBlockContext } from '#layers/forms/types/form-block';
import { computed, toValue } from 'vue';
import { useDataModelDisplayNames, usePrimitiveField } from '#layers/data-model/public';

export interface UseFormFieldValueReturn {
	/** Current value of the source field in the form state, or null if unavailable. */
	value: ComputedRef<unknown>;
	/** Resolved primitive field definition, or undefined if id is missing or not found. */
	fieldDef: ComputedRef<ResolvedMonospacePrimitiveField | undefined>;
	/** Display name of the source field (from meta.displayName or formatted apiName), or empty string if not found. */
	displayName: ComputedRef<string>;
	/** True when a source field id is set but the field is not found in the schema. */
	isMissing: ComputedRef<boolean>;
}

/**
 * Resolves a primitive field by id and returns its current value from the form block state.
 * Use this when a block needs to read another field's value in the same form (e.g. slug from input title).
 *
 * @param formBlockContext - Injected form block context (e.g. from injectFormBlockContext).
 * @param sourceFieldId - Primitive field id to read, or null/undefined if none.
 */
export function useFormFieldValue(
	formBlockContext: FormBlockContext | null,
	sourceFieldId: MaybeRefOrGetter<GlobalPrimitiveFieldId | null | undefined>,
): UseFormFieldValueReturn {
	const { getPrimitiveFieldDisplayName } = useDataModelDisplayNames();

	const fieldDef = usePrimitiveField(sourceFieldId);

	const displayName = computed(() =>
		fieldDef.value ? getPrimitiveFieldDisplayName(fieldDef.value) : '',
	);

	const isMissing = computed(() => {
		const id = toValue(sourceFieldId);
		return !!id && !fieldDef.value;
	});

	const value = computed(() => {
		if (isMissing.value || !fieldDef.value || !formBlockContext?.state.value) return null;
		return formBlockContext.state.value[fieldDef.value.apiName];
	});

	return {
		value,
		fieldDef,
		displayName,
		isMissing,
	};
}
